// Directory tree for the chat rail: the org hierarchy turned into drill-down levels, so a node is
// identified by WHERE it sits rather than by a label. Two agents sharing a name land in different
// teams and are therefore never in the same list.
//
// Angular-free on purpose, like organigram-graph-normalize: importing the component or
// OrganigramApiService pulls in CustomerdatabaseService and its import cycle, which crashes the
// Karma bootstrap. Keeping the logic here makes it unit-testable.

export interface DirNode {
  id: string;
  name: string;
  type?: string;
  parentId?: string;
  parentName?: string;
  children: DirNode[];
  isContainer: boolean;   // a grouping node: drill into it instead of chatting with it
  memberCount: number;    // agents/humans below it, at any depth - the row badge
}

export interface DirTree {
  roots: DirNode[];
  all: DirNode[];                  // flat, for search
  byId: Map<string, DirNode>;
}

// One rendered line of the indented tree.
export interface DirRow {
  node: DirNode;
  depth: number;
  expandable: boolean;   // has children, so the row opens instead of starting a chat
  expanded: boolean;
}

// Grouping layers of the organigram (root -> department -> division -> team -> member).
const CONTAINER_TYPES = ['team', 'department', 'division'];

// Unknown types count as members: the graph list endpoint may omit `type`, and a node the user can
// chat with is a safer default than one that swallows the click by drilling into nothing.
export function isContainerType(type?: string): boolean {
  return CONTAINER_TYPES.includes((type || '').toLowerCase());
}

// A human node is a PERSON, not a recipient the organigram can talk to: a message aimed at one
// lands in dailyTodos as a task, which is not a conversation and was never meant to be reachable
// from the chat surfaces. Agents and grouping layers stay chattable.
//
// Only an explicit 'human' counts. An unknown or missing type stays chattable, matching
// isContainerType's reasoning above - the list endpoint sometimes omits `type`, and silently
// dropping a real agent is worse than leaving one extra row.
export function isHumanNodeType(type?: string): boolean {
  return (type || '').toLowerCase() === 'human';
}

// A trigger is not a chat recipient either: it is the START of a workflow - a clock, a webhook or a
// document change - not something that reads a message, so a conversation aimed at one has no reader.
// Only an explicit 'trigger' counts, matching isHumanNodeType: an unknown or missing type stays
// chattable rather than silently dropping a real agent.
export function isTriggerNodeType(type?: string): boolean {
  return (type || '').toLowerCase() === 'trigger';
}

// A pipeline is not a chat recipient either: it is a data FEED into an agent - fetched, mapped and
// handed on - not something that reads a message, so a conversation aimed at one has no reader.
// Only an explicit 'pipeline' counts, for the same reason as its two neighbours: an unknown or
// missing type stays chattable rather than silently dropping a real agent.
export function isPipelineNodeType(type?: string): boolean {
  return (type || '').toLowerCase() === 'pipeline';
}

// The single rule every chat surface shares: who can be messaged at all. A human (whose message
// becomes a dailyTodos task), a trigger (a workflow starter with no reader) and a pipeline (a data
// feed with no reader) are the three node types that cannot, so all are hidden as chat targets and
// refused by the dock's backstop. Agents and grouping layers - and any unknown type - stay
// chattable, for the reason above.
export function isChattableNodeType(type?: string): boolean {
  return !isHumanNodeType(type) && !isTriggerNodeType(type) && !isPipelineNodeType(type);
}

// Build the drill-down tree from a graph response. Edges are directed parent -> child; a node may
// carry several parents (see OrganigramApiService.addParent), and the first edge wins so the result
// stays a tree. Cyclic or orphaned data still yields a usable tree - see attachRoots.
export function buildDirectoryTree(graph: { nodes?: any[]; edges?: any[] } | null): DirTree {
  const byId = new Map<string, DirNode>();
  for (const n of activeNodes(graph?.nodes || [])) {
    if (!n?.id || byId.has(n.id)) { continue; }
    byId.set(n.id, {
      id: n.id, name: n.name || '', type: n.type,
      children: [], isContainer: isContainerType(n.type), memberCount: 0
    });
  }

  for (const e of graph?.edges || []) {
    const parent = e?.source && byId.get(e.source);
    const child = e?.target && byId.get(e.target);
    if (!parent || !child || parent === child || child.parentId) { continue; }
    child.parentId = parent.id;
    child.parentName = parent.name;
    parent.children.push(child);
  }

  const all = Array.from(byId.values());
  for (const node of all) { node.children.sort(compareRows); }
  const roots = attachRoots(all);
  for (const root of roots) { countMembers(root, new Set<string>()); }
  return { roots, all, byId };
}

// What the rail lists at indent 0. A lone root is the company itself: an indent level holding one
// row helps nobody, so the listing starts inside it.
export function topLevel(tree: DirTree): DirNode[] {
  const roots = tree?.roots || [];
  return roots.length === 1 && roots[0].children.length ? roots[0].children : roots;
}

// The tree flattened to the rows currently visible, depth-first, honouring what the user has opened.
// Expandability is taken from the CHILDREN, never from `type`: the graph list response may omit it
// (OrganigramNodeSummary.type is optional), and a rail that hides members because a field is missing
// is worse than one that offers a chat with an empty team. `seen` keeps a cyclic graph terminating.
export function visibleRows(tree: DirTree, isExpanded: (id: string) => boolean): DirRow[] {
  const rows: DirRow[] = [];
  const seen = new Set<string>();
  const walk = (nodes: DirNode[], depth: number) => {
    for (const node of nodes) {
      if (seen.has(node.id)) { continue; }
      seen.add(node.id);
      const expandable = node.children.length > 0;
      // A childless human, trigger or pipeline is dropped: a row that is not expandable IS a chat
      // target, and none of them is one. Kept when it has reports, because the row then opens its
      // subtree instead of starting a chat - hiding it would take that whole branch off the tree.
      if (!expandable && !isChattableNodeType(node.type)) { continue; }
      const expanded = expandable && isExpanded(node.id);
      rows.push({ node, depth, expandable, expanded });
      if (expanded) { walk(node.children, depth + 1); }
    }
  };
  walk(topLevel(tree), 0);
  return rows;
}

// Ancestors of a node, outermost first (excluding the node itself). Used to open a search hit in
// place: every ancestor is expanded so the row is reachable in the tree.
export function ancestorsOf(tree: DirTree, nodeId: string): DirNode[] {
  const chain: DirNode[] = [];
  const seen = new Set<string>([nodeId]);
  let current = tree.byId.get(nodeId);
  while (current?.parentId && !seen.has(current.parentId)) {
    seen.add(current.parentId);
    const parent = tree.byId.get(current.parentId);
    if (!parent) { break; }
    chain.unshift(parent);
    current = parent;
  }
  return chain;
}

// Deleting a node orphans its subtree and flags it inactive (see OrganigramApiService.deleteNode), so
// the graph response carries leftovers with no parent edge. Left in, each one becomes a top-level row
// AND pushes the root count past one, which stops the rail from opening inside the company - the real
// teams end up hidden a level down behind a pile of deleted nodes. `active` is optional on the list
// response, so only an explicit false counts. If that leaves nothing, keep everything rather than
// render an empty rail (same fallback as buildStructure in org-standard-v2.graph).
function activeNodes(nodes: any[]): any[] {
  const active = nodes.filter(n => n?.active !== false);
  return active.length ? active : nodes;
}

// Containers first, then members, each A-Z: a team's sub-teams read as the structure, its people as
// the contents. localeCompare so Umlaute sort where a German reader expects them.
function compareRows(a: DirNode, b: DirNode): number {
  if (a.isContainer !== b.isContainer) { return a.isContainer ? -1 : 1; }
  return (a.name || '').localeCompare(b.name || '', 'de');
}

// Parentless nodes are the roots. Anything left unreachable from them sits in a cycle (a -> b -> a
// leaves neither parentless), so it is promoted to a root too: bad data costs the user a stray
// top-level row, never an empty rail or a hung tab.
function attachRoots(all: DirNode[]): DirNode[] {
  const roots = all.filter(n => !n.parentId);
  const reached = new Set<string>();
  const visit = (node: DirNode) => {
    if (reached.has(node.id)) { return; }
    reached.add(node.id);
    for (const child of node.children) { visit(child); }
  };
  for (const root of roots) { visit(root); }
  for (const node of all) {
    if (reached.has(node.id)) { continue; }
    roots.push(node);
    visit(node);
  }
  return roots.sort(compareRows);
}

// Members (non-containers) below a node, at any depth. `seen` keeps a cyclic graph from recursing
// forever; a node already counted on this path contributes nothing further.
function countMembers(node: DirNode, seen: Set<string>): number {
  if (seen.has(node.id)) { return 0; }
  seen.add(node.id);
  let count = 0;
  for (const child of node.children) {
    count += child.isContainer ? countMembers(child, seen) : 1;
  }
  node.memberCount = count;
  return count;
}
